<template>
  <div style="display: inline-flex; align-items: center; gap: 12px;">
    <span style="font-size: 14px; font-style: italic; opacity: 0.8;">Download</span>
    <span 
      style="cursor: pointer; font-size: 14px; font-weight: 600; color: white; opacity: 0.9;" 
      @click="toggleFormat"
    >
      {{ format.toUpperCase() }}
    </span>
    <v-btn 
      :class="['btn-secondary', format === 'pdf' ? 'active-button' : '']" 
      style="padding: 0 16px; height: 32px; background-color: rgba(255, 255, 255, 0.2); color: white; border: 1px solid white;" 
      @click="exportData"
    >
      <v-icon size="20">mdi-download</v-icon>
    </v-btn>
  </div>
</template>

<script>
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import Papa from 'papaparse';

export default {
name: 'ExportPayments',
props: {
  models: {
    type: Array,
    required: true,
    default: () => []
  },
  period: {
    type: String,
    default: 'All'
  },
  usdXe: {
    type: Number,
    default: 4000
  },
  eurXe: {
    type: Number,
    default: 4000
  }
},
data() {
  return {
    format: 'pdf',
    lastExported: 'N/A'
  };
},
computed: {
  studioSuffix() {
    try {
      const user = JSON.parse(localStorage.getItem('user'));
      const id = user?.studio_id;
      const studioMap = {
        1: 'CC',
        2: 'CJ',
        3: 'BOG',
        4: 'PEI'
      };
      return studioMap[id] || 'Unknown';
    } catch {
      return 'Unknown';
    }
  }
},
methods: {
  toggleFormat() {
    this.format = this.format === 'pdf' ? 'csv' : 'pdf';
  },
  exportData() {
    if (this.format === 'pdf') {
      this.downloadPDF();
    } else {
      this.downloadCSV();
    }
    this.lastExported = new Date().toLocaleString('en-US', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
    this.$emit('exported', this.lastExported);
  },
  formatCurrency(value, currency, hideSymbol = false) {
    if (value === null || value === undefined || isNaN(value)) return '0.00';
    if (currency !== 'COP') {
      return new Intl.NumberFormat('en-US', {
        style: hideSymbol ? 'decimal' : 'currency',
        currency: currency || 'USD',
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      }).format(value);
    }
    return new Intl.NumberFormat('en-US', {
      style: hideSymbol ? 'decimal' : 'currency',
      currency: 'COP',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(value);
  },
  displayCommission(commission) {
    if (commission === null || commission === undefined || isNaN(commission)) return '0%';
    return `${(commission).toFixed(0)}%`;
  },
  hasAdjustments(model) {
    return model.adjustments && model.adjustments.length > 0;
  },
  hasDeductions(model) {
    return model.deductions && model.deductions.length > 0;
  },
  translateType(t) {
    const map = {
      Purchase: 'Compras',
      Discount: 'Descuentos',
      Adjustment: 'Ajustes',
      Bonus: 'Bonos',
      'Site Bonus': 'Bono Sitio',
      'Studio Bonus': 'Bono Estudio',
      Subsidy: 'Subsidio'
    };
    return map[t] || (t || 'N/A');
  },

  async downloadPDF() {
    if (!this.models || this.models.length === 0) {
      return;
    }

    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const logoUrl = '/logo_mgroup.png';
    let logoImg;

    try {
      const response = await fetch(logoUrl);
      if (!response.ok) throw new Error('Failed to fetch logo');
      const blob = await response.blob();
      const reader = new FileReader();
      logoImg = await new Promise((resolve) => {
        reader.onload = () => resolve(reader.result);
        reader.readAsDataURL(blob);
      });
    } catch (error) {
      console.error('Error loading logo image:', error?.message || error);
      logoImg = null;
    }

    doc.bigText = () => doc.setFontSize(14);
    doc.normalText = () => doc.setFontSize(10);
    doc.smallText = () => doc.setFontSize(7);
    doc.tinyText = () => doc.setFontSize(6);

    const renderModelPDF = (doc, model, yOffsetStart) => {
      let yOffset = yOffsetStart;
      const colUnit = (pageWidth - 20) / 12;

      doc.setFont('helvetica', 'bold');
      doc.smallText();
      doc.text(model.fullName || 'N/A', 10, yOffset);
      yOffset += 4;

      doc.setFont('helvetica', 'normal');
      doc.text(model.stageName || 'N/A', 10, yOffset);
      doc.text(`TRM USD: ${(model.trmUSD || this.usdXe || 0).toLocaleString('en-US')}`, 10 + colUnit * 4, yOffset);
      doc.text(`Fecha: ${model.paymentDate || 'N/A'}`, 10 + colUnit * 7, yOffset);
      yOffset += 4;

      doc.smallText();
      doc.text(`${model.docType || 'Documento'}: ${model.docNumber || 'N/A'}`, 10, yOffset);
      doc.text(`TRM EUR: ${(model.trmEUR || this.eurXe || 0).toLocaleString('en-US')}`, 10 + colUnit * 4, yOffset);
      doc.text(`Periodo: ${model.period || 'N/A'}`, 10 + colUnit * 7, yOffset);

      const logoX = pageWidth - 40;
      const logoY = yOffsetStart - 4;
      if (logoImg) {
        const maxWidthPdf = 300 * 0.0353 * 2.83465;
        const maxHeightPdf = 140 * 0.0353 * 2.83465;
        doc.addImage(logoImg, 'PNG', logoX, logoY, maxWidthPdf, maxHeightPdf);
      }

      yOffset += 4;
      doc.setLineWidth(0.5);
      doc.line(10, yOffset, pageWidth - 10, yOffset);

      yOffset += 7;
      doc.setFontSize(12);
      doc.text('Ganancias', 10, yOffset);
      yOffset += 2;
      const earningsBody = (model.earnings || []).map(earn => [
        earn.platform || 'N/A',
        earn.period || 'N/A',
        earn.currency || 'N/A',
        `${earn.earnings < 0 ? '-' : ''}$${Math.abs(earn.earnings || 0).toLocaleString('en-US')}`,
        this.displayCommission(earn.commission || 0),
        `${earn.copTotal < 0 ? '-' : ''}$${Math.floor(Math.abs(earn.copTotal || 0)).toLocaleString('en-US')}`
      ]);
      autoTable(doc, {
        startY: yOffset,
        head: [['Plataforma', 'Periodo', 'Moneda', 'Ganancia', 'Comisión', 'Total COP']],
        body: earningsBody,
        styles: { fontSize: 10, textColor: [0, 0, 0] },
        headStyles: { fillColor: [255, 255, 255], textColor: [0, 0, 0], fontStyle: 'bold', fontSize: 11, halign: 'left' },
        bodyStyles: { fillColor: [255, 255, 255] },
        alternateRowStyles: { fillColor: [255, 255, 255] },
        columnStyles: {
          5: { cellWidth: 25 } // Fix width of Total COP column (index 5)
        }
      });
      yOffset = doc.lastAutoTable.finalY;

      if (this.hasAdjustments(model)) {
        yOffset += 6;
        doc.text('Ajustes', 10, yOffset);
        yOffset += 2;
        const adjBody = model.adjustments.map(adj => [
          this.translateType(adj.type),
          `${adj.amount < 0 ? '-' : ''}$${Math.abs(adj.amount || 0).toLocaleString('en-US')}`,
          adj.currency || 'N/A',
          adj.description || 'N/A',
          adj.number || 'N/A',
          adj.quotas || 'N/A',
          adj.date || 'N/A',
          `${((adj.copTotal || 0) * (adj.sign || 1)) < 0 ? '-' : ''}$${Math.floor(Math.abs((adj.copTotal || 0) * (adj.sign || 1))).toLocaleString('en-US')}`
        ]);
        autoTable(doc, {
          startY: yOffset,
          head: [['Tipo', 'Monto', 'Moneda', 'Descripción', '#', 'Cuotas', 'Fecha', 'Total COP']],
          body: adjBody,
          styles: { fontSize: 10, textColor: [0, 0, 0] },
          headStyles: { fillColor: [255, 255, 255], textColor: [0, 0, 0], fontStyle: 'bold', fontSize: 11, halign: 'left' },
          bodyStyles: { fillColor: [255, 255, 255] },
          alternateRowStyles: { fillColor: [255, 255, 255] },
          columnStyles: {
            3: { cellWidth: 50 }, // Set fixed width for Descripción
            7: { cellWidth: 25 }  // Total COP
          }
        });
        yOffset = doc.lastAutoTable.finalY;
      }

      if (this.hasDeductions(model)) {
        yOffset += 6;
        doc.text('Deducciones', 10, yOffset);
        yOffset += 2;
        const dedBody = model.deductions.map(ded => [
          this.translateType(ded.type),
          `${ded.amount < 0 ? '-' : ''}$${Math.abs(ded.amount || 0).toLocaleString('en-US')}`,
          ded.currency || 'N/A',
          ded.description || 'N/A',
          ded.number || 'N/A',
          ded.quotas || 'N/A',
          ded.date || 'N/A',
          `${((ded.copTotal || 0) * (ded.sign || 1)) < 0 ? '-' : ''}$${Math.floor(Math.abs((ded.copTotal || 0) * (ded.sign || 1))).toLocaleString('en-US')}`
        ]);

        // Add retention if applicable
        if (['Yes', 'ALWAYS'].includes(model.retention_status)) {
          const retentionAmount = Math.floor((model.gross_earnings || 0) * 0.04);
          dedBody.push([
            'Descuento',
            '4%',
            'COP',
            'Retención de la fuente',
            '1',
            '1',
            model.paymentDate || 'N/A',
            `-$${retentionAmount.toLocaleString('en-US')}`
          ]);
        }
        
        autoTable(doc, {
          startY: yOffset,
          head: [['Tipo', 'Monto', 'Moneda', 'Descripción', '#', 'Cuotas', 'Fecha', 'Total COP']],
          body: dedBody,
          styles: { fontSize: 10, textColor: [0, 0, 0] },
          headStyles: { fillColor: [255, 255, 255], textColor: [0, 0, 0], fontStyle: 'bold', fontSize: 11, halign: 'left' },
          bodyStyles: { fillColor: [255, 255, 255] },
          alternateRowStyles: { fillColor: [255, 255, 255] },
          columnStyles: {
            3: { cellWidth: 50 }, // Set fixed width for Descripción
            7: { cellWidth: 25 }  // Total COP
          }
        });
        yOffset = doc.lastAutoTable.finalY;
      }

      yOffset += 4;
      doc.line(10, yOffset, pageWidth - 10, yOffset);
      yOffset += 2;

      const lineY = yOffset + 6.5;
      const colWidth = (pageWidth - 20) / 12;

      doc.line(10, lineY, 10 + colWidth * 3, lineY);
      doc.tinyText();
      doc.text('Firmar', 10, lineY + 3);

      const cedulaX = 10 + colWidth * 3 + 10;
      doc.line(cedulaX, lineY, cedulaX + colWidth * 3, lineY);
      doc.text('Cédula', cedulaX, lineY + 3);

      doc.setFontSize(10);
      doc.setFont('helvetica', 'bold');
      const totalCopX = pageWidth - 15;
      const retentionDeduction = ['Yes', 'ALWAYS'].includes(model.retention_status)
        ? Math.floor((model.gross_earnings || 0) * 0.04)
        : 0;

      const earningsTotal = (model.earnings || [])
        .reduce((sum, e) => sum + Math.floor(e.copTotal || 0), 0);

      const adjustmentsTotal = (model.adjustments || [])
        .reduce((sum, a) => sum + Math.floor(((a.copTotal || 0) * (a.sign || 1))), 0);

      const deductionsTotal = (model.deductions || [])
        .reduce((sum, d) => sum + Math.floor(((d.copTotal || 0) * (d.sign || 1))), 0);

      const netTotal = earningsTotal + adjustmentsTotal + deductionsTotal - retentionDeduction;
      doc.text(`Ganancias Totales: $${netTotal.toLocaleString('en-US')}`, totalCopX, lineY, { align: 'right' });
    };

   this.models.forEach((model, index) => {
  // Always start a fresh page per model (except on very first)
  if (index > 0) doc.addPage();

  const pagesBefore = doc.getNumberOfPages();
  const startY = 10;

  // Render first copy
  renderModelPDF(doc, model, startY);

  const pagesAfter = doc.getNumberOfPages();
  const pageHeight = doc.internal.pageSize.getHeight();
  const yUsed = doc.lastAutoTable?.finalY || startY;

  // Be conservative: only allow same-page if first copy ended in top 45% of the page
  const threshold = pageHeight * 0.45;

  const stayedOnSamePage = (pagesAfter === pagesBefore);
  const fitsForSecondHalf = stayedOnSamePage && (yUsed <= threshold);

  if (fitsForSecondHalf) {
    // same page: draw cut guide and render second copy below
    const cutGuideY = yUsed + 20;
    doc.setDrawColor(200);
    doc.setLineWidth(0.1);
    doc.setLineDash([1, 1], 0);
    doc.line(10, cutGuideY, doc.internal.pageSize.getWidth() - 10, cutGuideY);
    doc.setLineDash([]);
    renderModelPDF(doc, model, cutGuideY + 6);
  } else {
    doc.addPage();
    renderModelPDF(doc, model, 10);
  }
});

const studioSuffix = this.studioSuffix;
const pdfName = `Pago Estudio ${this.models[0]?.period || 'Periodo'} (${studioSuffix}).pdf`;
doc.save(pdfName);
},

  downloadCSV() {
    // Note: In Excel, apply dark grey background (#A9A9A9) and bold font to rows with headers:
    // - 'Nombre', 'Tipo', 'Plataforma', 'Periodo', 'Moneda', 'Ventas', 'Commission', 'Total COP'
    // - 'Tipo', 'Fecha', 'Description', 'Moneda', 'Numero', 'Quota', 'Total COP'
    // Apply bold font to rows with 'Total Ganancias' and its TotalCOP value.
    const csvData = [];

    this.models.forEach(model => {
      // Add main header (bold and dark grey in Excel)
      csvData.push({
        Nombre: 'NOMBRE',
        Tipo: 'TIPO',
        Plataforma: 'PLATAFORMA',
        Periodo: 'PERIODO',
        Moneda: 'MONEDA',
        Ventas: 'VENTAS',
        Commission: 'COMMISSION',
        TotalCOP: 'TOTAL COP'
      });

      // Add model details and earnings
      csvData.push({
  Nombre: model.fullName || '',
  Tipo: 'Ganancias',
  Plataforma: model.earnings[0]?.platform || '',
  Periodo: model.earnings[0]?.period || '',
  Moneda: model.earnings[0]?.currency || '',
  Ventas: model.earnings[0] ? model.earnings[0].earnings : '',
  Commission: model.earnings[0] ? this.displayCommission(model.earnings[0].commission) : '',
  TotalCOP: model.earnings[0] ? Math.floor(model.earnings[0].copTotal || 0).toString() : ''
});

     csvData.push({
  Nombre: model.stageName || '',
  Tipo: 'Ganancias',
  Plataforma: model.earnings[1]?.platform || '',
  Periodo: model.earnings[1]?.period || '',
  Moneda: model.earnings[1]?.currency || '',
  Ventas: model.earnings[1] ? model.earnings[1].earnings : '',
  Commission: model.earnings[1] ? this.displayCommission(model.earnings[1].commission) : '',
  TotalCOP: model.earnings[1] ? Math.floor(model.earnings[1].copTotal || 0).toString() : ''
});

      csvData.push({
  Nombre: `Cedula: ${model.docNumber || ''}`,
  Tipo: 'Ganancias',
  Plataforma: model.earnings[2]?.platform || '',
  Periodo: model.earnings[2]?.period || '',
  Moneda: model.earnings[2]?.currency || '',
  Ventas: model.earnings[2] ? model.earnings[2].earnings : '',
  Commission: model.earnings[2] ? this.displayCommission(model.earnings[2].commission) : '',
  TotalCOP: model.earnings[2] ? Math.floor(model.earnings[2].copTotal || 0).toString() : ''
});

      // Add remaining earnings
      model.earnings.slice(3).forEach(earn => {
        csvData.push({
          Nombre: '',
          Tipo: 'Ganancias',
          Plataforma: earn.platform || 'N/A',
          Periodo: earn.period || 'N/A',
          Moneda: earn.currency || 'N/A',
          Ventas: earn.earnings || 0,
          Commission: this.displayCommission(earn.commission),
          TotalCOP: Math.floor(earn.copTotal || 0).toString()
        });
      });

      // Add adjustments and deductions sub-header if applicable (bold and dark grey in Excel)
      if (this.hasAdjustments(model) || this.hasDeductions(model)) {
        csvData.push({
          Nombre: '',
          Tipo: 'TIPO',
          Plataforma: 'FECHA',
          Periodo: 'DESCRIPCIÓN',
          Moneda: 'MONEDA',
          Ventas: 'NÚMERO',
          Commission: 'CUOTA',
          TotalCOP: 'TOTAL COP'
        });
      }

      // Add adjustments
      model.adjustments.forEach(adj => {
        csvData.push({
          Nombre: '',
          Tipo: this.translateType(adj.type),
          Plataforma: adj.date || 'N/A',
          Periodo: adj.description === 'Transportation Subsidy' ? 'Subsidio de Transporte' : (adj.description || 'N/A'),
          Moneda: adj.currency || 'N/A',
          Ventas: adj.number || 'N/A',
          Commission: adj.quotas || 'N/A',
          TotalCOP: Math.floor((adj.copTotal || 0) * (adj.sign || 1)).toString()
        });
      });

      // Add retention first if applicable
      if (['Yes', 'ALWAYS'].includes(model.retention_status)) {
        const retentionAmount = Math.floor((model.gross_earnings || 0) * 0.04);
        csvData.push({
          Nombre: '',
          Tipo: 'Descuento',
          Plataforma: model.paymentDate || 'N/A',
          Periodo: 'Retención de la fuente',
          Moneda: 'COP',
          Ventas: '1',
          Commission: '1',
          TotalCOP: (-retentionAmount).toString()
        });
      }

      // Add deductions
      model.deductions.forEach(ded => {
        csvData.push({
          Nombre: '',
          Tipo: this.translateType(ded.type),
          Plataforma: ded.date || 'N/A',
          Periodo: ded.description || 'N/A',
          Moneda: ded.currency || 'N/A',
          Ventas: ded.number || 'N/A',
          Commission: ded.quotas || 'N/A',
          TotalCOP: Math.floor((ded.copTotal || 0) * (ded.sign || 1)).toString()
        });
      });
      
      // Add total earnings (bold in Excel)
      const retentionDeduction = ['Yes', 'ALWAYS'].includes(model.retention_status)
        ? Math.floor((model.gross_earnings || 0) * 0.04)
        : 0;

      const earningsTotal = (model.earnings || [])
        .reduce((sum, e) => sum + Math.floor(e.copTotal || 0), 0);

      const adjustmentsTotal = (model.adjustments || [])
        .reduce((sum, a) => sum + Math.floor(((a.copTotal || 0) * (a.sign || 1))), 0);

      const deductionsTotal = (model.deductions || [])
        .reduce((sum, d) => sum + Math.floor(((d.copTotal || 0) * (d.sign || 1))), 0);

      const netTotal = earningsTotal + adjustmentsTotal + deductionsTotal - retentionDeduction;
      csvData.push({
        Nombre: '',
        Tipo: 'NET PAGADO',
        Plataforma: '',
        Periodo: '',
        Moneda: '',
        Ventas: '',
        Commission: '',
        TotalCOP: netTotal.toString()
      });

      // Add blank row for separation
      csvData.push({});
    });

    const csv = Papa.unparse(csvData, {
      columns: ['Nombre', 'Tipo', 'Plataforma', 'Periodo', 'Moneda', 'Ventas', 'Commission', 'TotalCOP']
    });
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    const studioSuffix = this.studioSuffix;
    const fileName = `Pago Estudio ${this.models[0]?.period || 'Periodo'} (${studioSuffix}).csv`;
    link.setAttribute('download', fileName);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }
}
};
</script>
